import { useState, useRef, useEffect } from 'react';
import { Lock, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface PinVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  moduleLabel?: string;
}

const CORRECT_PIN = import.meta.env.VITE_SECURITY_PIN || '9090';

export const PinVerificationModal = ({
  isOpen,
  onClose,
  onSuccess,
  moduleLabel,
}: PinVerificationModalProps) => {
  const [pin, setPin] = useState<string[]>(['', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  useEffect(() => {
    if (isOpen) {
      setPin(['', '', '', '']);
      setError(null);
      setIsShaking(false);
      setTimeout(() => {
        inputRefs[0].current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleChange = (value: string, index: number) => {
    // Only allow numeric input
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError(null);

    // Auto-focus next input if a digit is entered
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Submit if all 4 digits are filled
    if (newPin.every((digit) => digit !== '')) {
      const fullPin = newPin.join('');
      if (fullPin === CORRECT_PIN) {
        onSuccess();
      } else {
        setError('Incorrect PIN. Please try again.');
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
        setPin(['', '', '', '']);
        inputRefs[0].current?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!pin[index] && index > 0) {
        // Focus previous input and clear it
        const newPin = [...pin];
        newPin[index - 1] = '';
        setPin(newPin);
        inputRefs[index - 1].current?.focus();
      } else {
        // Clear current input
        const newPin = [...pin];
        newPin[index] = '';
        setPin(newPin);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (!/^\d{4}$/.test(pasteData)) return;

    const digits = pasteData.split('');
    setPin(digits);

    if (pasteData === CORRECT_PIN) {
      onSuccess();
    } else {
      setError('Incorrect PIN. Please try again.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      setPin(['', '', '', '']);
      inputRefs[0].current?.focus();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[400px] rounded-2xl border-border bg-card p-6 overflow-hidden">
        <div className="flex flex-col items-center text-center space-y-6 pt-4">
          <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary">
            <Lock className="h-6 w-6 animate-pulse" />
          </div>

          <div className="space-y-1">
            <DialogTitle className="text-lg font-black uppercase tracking-wider text-foreground">
              Secure Access Required
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground max-w-[280px]">
              Accessing{' '}
              {moduleLabel ? (
                <strong className="text-primary">{moduleLabel}</strong>
              ) : (
                'this module'
              )}{' '}
              requires entering a 4-digit security PIN.
            </DialogDescription>
          </div>

          <div
            className={`flex items-center gap-3 justify-center py-2 ${
              isShaking ? 'animate-bounce' : ''
            }`}
          >
            {pin.map((digit, idx) => (
              <input
                key={idx}
                ref={inputRefs[idx]}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                onPaste={idx === 0 ? handlePaste : undefined}
                className={`h-14 w-12 text-center text-xl font-bold rounded-xl border focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all bg-muted border-input text-foreground`}
                autoComplete="one-time-code"
                style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' } as React.CSSProperties}
              />
            ))}
          </div>

          {error && (
            <div className="flex items-center gap-1.5 text-destructive text-[11px] font-bold uppercase tracking-wider">
              <ShieldAlert className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}

          <div className="w-full pt-2">
            <Button
              variant="ghost"
              onClick={onClose}
              className="w-full h-11 rounded-xl font-bold text-xs uppercase tracking-widest text-muted-foreground hover:bg-accent transition-all"
            >
              Cancel Access
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PinVerificationModal;
